import { useQuery } from '@tanstack/react-query';

import { AxiosCartError, Cart, CartErrorTypes, CartSession, KnownReason } from '@telialt/models/src/EshopModel';
import { datadogLogger } from '@telialt/utils/src/datadog/datadogLogger';
import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { useLogError } from '@telialt/utils/src/datadog/useLogError';
import {
    PARAM_DISNEY,
    PARAM_GIFTS,
    PARAM_GUID_QUERY,
    PARAM_ID_QUERY,
    PARAM_NETFLIX,
    PARAM_SERVICE_SO_ID_QUERY,
    PARAM_SERVICE_UO_ID_QUERY,
    PARAM_SERVICE_UO_TYPE_QUERY,
    PARAM_TYPE_QUERY,
} from '@telialt/utils/src/eshopConstants';
import { getCartIdSync, getCartSession, isCartLoadedFromUrl, toCartError } from '@telialt/utils/src/eshopUtils';
import QueryString from '@telialt/utils/src/QueryString';
import { useSegment } from '@telialt/utils/src/segment';
import { getIsUserAuthorized } from '@telialt/utils/src/SSO/utils';

import { ESHOP_QUERY_KEY } from '../../model';
import { apiGetCart, apiMigrateCart } from '../../store/api';

const isCartNotFoundError = (error: unknown) => {
    const cartError = toCartError(error as AxiosCartError);

    return cartError.type === CartErrorTypes.CartError && cartError.reason === KnownReason.NotFound;
};

const isRecoverableWithSessionCart = (
    error: unknown,
    cartSession: CartSession | null,
    cartIdFromUrl: string | undefined,
) =>
    isCartNotFoundError(error) &&
    getIsUserAuthorized() &&
    !!cartSession &&
    getCartIdSync(cartSession) !== cartIdFromUrl;

export const useStrategyLoadCartQuery = () => {
    const segment = useSegment();

    const query = useQuery<Cart, AxiosCartError>({
        queryKey: [ESHOP_QUERY_KEY.CART],
        queryFn: async ({ signal }) => {
            const { search } = window.location;
            const type = new QueryString(search).getParam(PARAM_TYPE_QUERY);
            const serviceUoId = new QueryString(search).getParam(PARAM_SERVICE_UO_ID_QUERY);
            const serviceSoId = new QueryString(search).getParam(PARAM_SERVICE_SO_ID_QUERY);
            const serviceUoType = new QueryString(search).getParam(PARAM_SERVICE_UO_TYPE_QUERY);
            const gifts = new QueryString(search).getParam(PARAM_GIFTS);
            const disney = new QueryString(search).getParam(PARAM_DISNEY);
            const netflix = new QueryString(search).getParam(PARAM_NETFLIX);
            const cartSession = getCartSession();

            if (type && serviceUoId) {
                return (
                    await apiMigrateCart(segment, {
                        type,
                        serviceUoId,
                        serviceSoId,
                        UOType: serviceUoType,
                        gifts,
                        disney,
                        netflix,
                    })
                ).data;
            }

            if (isCartLoadedFromUrl(search)) {
                const queryString = new QueryString(search);
                const guid = queryString.getParam(PARAM_GUID_QUERY);
                const code = queryString.getParam(PARAM_ID_QUERY);
                const cartIdFromUrl = guid || code;

                try {
                    return (await apiGetCart(segment, { cartId: cartIdFromUrl })).data;
                } catch (error) {
                    if (!isRecoverableWithSessionCart(error, cartSession, cartIdFromUrl)) {
                        throw error;
                    }

                    datadogLogger().info('Cart from url is not found, falling back to cart in session', {
                        component: DDComponent.Eshop,
                        cartSession,
                        cartIdFromUrl,
                    });
                }
            }

            if (cartSession == null) {
                throw Error('cart session is empty, defaulting to "errorPlaceholder"');
            }

            if (cartSession?.isCartAnonymous && getIsUserAuthorized()) {
                return (await apiMigrateCart(segment, { oldCartId: cartSession.guid })).data;
            }

            const cartIdFromSession = getCartIdSync(cartSession);

            return (await apiGetCart(segment, { cartId: cartIdFromSession }, null, signal)).data;
        },
        staleTime: 3000,
        retryOnMount: false,
        refetchOnMount: true,
    });

    useLogError(query.error, DDComponent.Eshop);

    return query;
};
